import { findParentNode } from '@tiptap/core'
import { Selection, Transaction } from '@tiptap/pm/state'
import { CellSelection, Rect, TableMap } from '@tiptap/pm/tables'
import { Fragment, Node, ResolvedPos, Schema } from '@tiptap/pm/model'
import { normalizeTextColor } from "@defectwise/pdf-report-core";

const HEX_COLOR = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGBA_COLOR = /^rgba?\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*(?:,\s*([\d.]+)\s*)?\)$/i;

/**
 * Shared by TableCell and TableHeader's `backgroundColor` attr parseHTML — the browser's CSSOM
 * canonicalizes style.backgroundColor to rgb(...) on read regardless of how it was set, so a
 * Ctrl+C/Ctrl+V round-trip (ProseMirror re-parses its own clipboard HTML through this same rule)
 * turned every hex cell colour into rgb (see Color.ts's parseHTML for the identical fix already
 * applied to text colour).
 *
 * `normalizeTextColor` falls back to opaque "black" for anything it can't parse as hex/rgb(a) —
 * the right default for its original text-colour use case, but wrong for a background: a pasted
 * "transparent" keyword, an unrecognized CSS colour, or a fully-transparent rgba() cell fill
 * should end up with no fill, not a cell painted solid black. `null` is the same "no colour" state
 * setCellsBackgroundColor(null) (the picker's own clear button) already produces, so parsing
 * lands in the exact state a user clearing the colour by hand would leave it in.
 */
export function parseBackgroundColorFromHTML(element: HTMLElement): string | null {
  const raw = element.style.backgroundColor?.replace(/['"]+/g, "");
  if (!raw) return null;

  const rgbaMatch = raw.match(RGBA_COLOR);
  const alpha = rgbaMatch?.[1] !== undefined ? parseFloat(rgbaMatch[1]) : undefined;
  const isNoFill = (!HEX_COLOR.test(raw) && !rgbaMatch) || alpha === 0;

  return isNoFill ? null : normalizeTextColor(raw);
}

export const isRectSelected = (rect: Rect) => (selection: CellSelection) => {
  const map = TableMap.get(selection.$anchorCell.node(-1))
  const start = selection.$anchorCell.start(-1)
  const cells = map.cellsInRect(rect)
  const selectedCells = map.cellsInRect(
    map.rectBetween(selection.$anchorCell.pos - start, selection.$headCell.pos - start),
  )

  for (let i = 0, count = cells.length; i < count; i += 1) {
    if (selectedCells.indexOf(cells[i]) === -1) {
      return false
    }
  }

  return true
}

export const findTable = (selection: Selection) =>
  findParentNode(node => node.type.spec.tableRole && node.type.spec.tableRole === 'table')(selection)

export const isCellSelection = (selection: Selection): selection is CellSelection => selection instanceof CellSelection

export const isColumnSelected = (columnIndex: number) => (selection: Selection) => {
  if (isCellSelection(selection)) {
    const map = TableMap.get(selection.$anchorCell.node(-1))

    return isRectSelected({
      left: columnIndex,
      right: columnIndex + 1,
      top: 0,
      bottom: map.height,
    })(selection)
  }

  return false
}

export const isRowSelected = (rowIndex: number) => (selection: Selection) => {
  if (isCellSelection(selection)) {
    const map = TableMap.get(selection.$anchorCell.node(-1))

    return isRectSelected({
      left: 0,
      right: map.width,
      top: rowIndex,
      bottom: rowIndex + 1,
    })(selection)
  }

  return false
}

export const isTableSelected = (selection: Selection) => {
  if (isCellSelection(selection)) {
    const map = TableMap.get(selection.$anchorCell.node(-1))

    return isRectSelected({
      left: 0,
      right: map.width,
      top: 0,
      bottom: map.height,
    })(selection)
  }

  return false
}

export const getCellsInColumn = (columnIndex: number | number[]) => (selection: Selection) => {
  const table = findTable(selection)
  if (table) {
    const map = TableMap.get(table.node)
    const indexes = Array.isArray(columnIndex) ? columnIndex : Array.from([columnIndex])

    return indexes.reduce(
      (acc, index) => {
        if (index >= 0 && index <= map.width - 1) {
          const cells = map.cellsInRect({
            left: index,
            right: index + 1,
            top: 0,
            bottom: map.height,
          })

          return acc.concat(
            cells.map(nodePos => {
              const node = table.node.nodeAt(nodePos)
              const pos = nodePos + table.start

              return { pos, start: pos + 1, node }
            }),
          )
        }

        return acc
      },
      [] as { pos: number; start: number; node: Node | null | undefined }[],
    )
  }
  return null
}

export const getCellsInRow = (rowIndex: number | number[]) => (selection: Selection) => {
  const table = findTable(selection)

  if (table) {
    const map = TableMap.get(table.node)
    const indexes = Array.isArray(rowIndex) ? rowIndex : Array.from([rowIndex])

    return indexes.reduce(
      (acc, index) => {
        if (index >= 0 && index <= map.height - 1) {
          const cells = map.cellsInRect({
            left: 0,
            right: map.width,
            top: index,
            bottom: index + 1,
          })

          return acc.concat(
            cells.map(nodePos => {
              const node = table.node.nodeAt(nodePos)
              const pos = nodePos + table.start
              return { pos, start: pos + 1, node }
            }),
          )
        }

        return acc
      },
      [] as { pos: number; start: number; node: Node | null | undefined }[],
    )
  }

  return null
}

/** Cells covered by the current CellSelection's rectangle (e.g. a grip-selected row/column). */
export const getSelectionRectCells = (selection: CellSelection) => {
  const table = findTable(selection)

  if (table) {
    const map = TableMap.get(table.node)
    const start = selection.$anchorCell.start(-1)
    const rect = map.rectBetween(selection.$anchorCell.pos - start, selection.$headCell.pos - start)
    const cells = map.cellsInRect(rect)

    return cells.map(nodePos => {
      const node = table.node.nodeAt(nodePos)
      const pos = nodePos + table.start

      return { pos, start: pos + 1, node }
    })
  }

  return null
}

/** Cells affected by an arbitrary selection: the rect for a CellSelection, else the single enclosing cell. */
export const getCellsForSelection = (selection: Selection) => {
  if (isCellSelection(selection)) {
    return getSelectionRectCells(selection)
  }

  const cell = findCellClosestToPos(selection.$from)
  return cell ? [cell] : null
}

export const getCellsInTable = (selection: Selection) => {
  const table = findTable(selection)

  if (table) {
    const map = TableMap.get(table.node)
    const cells = map.cellsInRect({
      left: 0,
      right: map.width,
      top: 0,
      bottom: map.height,
    })

    return cells.map(nodePos => {
      const node = table.node.nodeAt(nodePos)
      const pos = nodePos + table.start

      return { pos, start: pos + 1, node }
    })
  }

  return null
}

/**
 * Adds or removes the `bold` mark on every text node in a cell's content — used to keep header-row
 * text in sync with `toggleHeaderRowShading`, since the pdf-report-core renderer reads bold off the
 * paragraph's own text marks rather than the tableHeader/tableCell node type.
 */
export const setBoldMarkOnFragment = (fragment: Fragment, schema: Schema, bold: boolean): Fragment => {
  const boldType = schema.marks.bold
  if (!boldType) return fragment

  const children: Node[] = []
  fragment.forEach(node => {
    if (node.isText) {
      const marks = bold ? boldType.create().addToSet(node.marks) : boldType.removeFromSet(node.marks)
      children.push(node.mark(marks))
    } else {
      children.push(node.content.size > 0 ? node.copy(setBoldMarkOnFragment(node.content, schema, bold)) : node)
    }
  })

  return Fragment.fromArray(children)
}

/** True if any cell in the table has colspan/rowspan > 1 — gates operations (like moveColumn) that don't yet handle spans. */
export const tableHasSpans = (table: Node): boolean => {
  let found = false
  table.forEach((row) => {
    row.forEach((cell) => {
      if (cell.attrs.colspan > 1 || cell.attrs.rowspan > 1) found = true
    })
  })
  return found
}

export const findParentNodeClosestToPos = ($pos: ResolvedPos, predicate: (node: Node) => boolean) => {
  for (let i = $pos.depth; i > 0; i -= 1) {
    const node = $pos.node(i)

    if (predicate(node)) {
      return {
        pos: i > 0 ? $pos.before(i) : 0,
        start: $pos.start(i),
        depth: i,
        node,
      }
    }
  }

  return null
}

export const findCellClosestToPos = ($pos: ResolvedPos) => {
  const predicate = (node: Node) => node.type.spec.tableRole && /cell/i.test(node.type.spec.tableRole)

  return findParentNodeClosestToPos($pos, predicate)
}

const select = (type: 'row' | 'column') => (index: number) => (tr: Transaction) => {
  const table = findTable(tr.selection)
  const isRowSelection = type === 'row'

  if (table) {
    const map = TableMap.get(table.node)

    // Check if the index is valid
    if (index >= 0 && index < (isRowSelection ? map.height : map.width)) {
      const left = isRowSelection ? 0 : index
      const top = isRowSelection ? index : 0
      const right = isRowSelection ? map.width : index + 1
      const bottom = isRowSelection ? index + 1 : map.height

      const cellsInFirstRow = map.cellsInRect({
        left,
        top,
        right: isRowSelection ? right : left + 1,
        bottom: isRowSelection ? top + 1 : bottom,
      })

      const cellsInLastRow =
        bottom - top === 1
          ? cellsInFirstRow
          : map.cellsInRect({
              left: isRowSelection ? left : right - 1,
              top: isRowSelection ? bottom - 1 : top,
              right,
              bottom,
            })

      const head = table.start + cellsInFirstRow[0]
      const anchor = table.start + cellsInLastRow[cellsInLastRow.length - 1]
      const $head = tr.doc.resolve(head)
      const $anchor = tr.doc.resolve(anchor)

      return tr.setSelection(new CellSelection($anchor, $head))
    }
  }
  return tr
}

export const selectColumn = select('column')

export const selectRow = select('row')

/** Absolute doc position of the cell a selection is in/on — grip-selected cell for a CellSelection, else the enclosing cell of a plain cursor. */
const getSelectedCellPos = (selection: Selection): number | null => {
  if (isCellSelection(selection)) return selection.$anchorCell.pos

  return findCellClosestToPos(selection.$from)?.pos ?? null
}

/** Column index of a grip-selected column (or the column containing a plain cursor), else null. */
export const getSelectedColumnIndex = (selection: Selection): number | null => {
  const table = findTable(selection)
  if (!table) return null

  const cellPos = getSelectedCellPos(selection)
  if (cellPos === null) return null

  return TableMap.get(table.node).findCell(cellPos - table.start).left
}

/** Row index of a grip-selected row (or the row containing a plain cursor), else null. */
export const getSelectedRowIndex = (selection: Selection): number | null => {
  const table = findTable(selection)
  if (!table) return null

  const cellPos = getSelectedCellPos(selection)
  if (cellPos === null) return null

  return TableMap.get(table.node).findCell(cellPos - table.start).top
}

export const selectTable = (tr: Transaction) => {
  const table = findTable(tr.selection)

  if (table) {
    const { map } = TableMap.get(table.node)

    if (map && map.length) {
      const head = table.start + map[0]
      const anchor = table.start + map[map.length - 1]
      const $head = tr.doc.resolve(head)
      const $anchor = tr.doc.resolve(anchor)

      return tr.setSelection(new CellSelection($anchor, $head))
    }
  }

  return tr
}
